import type {RawAudience} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {MetricInsightsPopupMenuItem} from './components/metric-insights/metric-insights.types';

export interface RecipientOpenInsight {
  idEmailMarketingCampaign: string;
  email: string;
  name: string;
}

enum MetricType {
  METRIC_TYPE_CLICK = 'click',
  METRIC_TYPE_OPEN = 'open',
  METRIC_TYPE_DELIVERY = 'delivery',
}

enum MetricItemType {
  CAMPAIGN = 'c',
  AUDIENCE = 'a',
}

export interface Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY | MetricType.METRIC_TYPE_OPEN | MetricType.METRIC_TYPE_CLICK;
  positiveOutcomeLabel: string;
  negativeOutcomeLabel: string;
  positiveOutcomePercentage: number;
  negativeOutcomePercentage: number;
  positiveOutcomeTotal: number;
  negativeOutcomeTotal: number;
  total: number;
}

export interface DeliveryMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY;
  negativeOutcomeDetails: {
    label: string;
    value: number;
  }[];
}

export interface OpenMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_OPEN;
}

export interface ClickMetrics extends Metrics {
  metricType: MetricType.METRIC_TYPE_DELIVERY;
}

export interface CampaignClickInsights {
  links: {name: string}[];
  showMoreLabel: string;
}

export interface CampaignMetricDetails {
  idCampaign: string;
  idMailingList: number;
  email: string;
  isDelivered: boolean;
  isBounce: boolean;
  isUnsubscribe: boolean;
  isComplaint: boolean;
  isMarkedAsSpam: boolean;
  isInvalid: boolean;
  isDeleted: boolean;
  numOpens: number;
  numClicks: number;
  clickInsights: CampaignClickInsights;
  openInsights: RecipientOpenInsight[];
}

export interface SingleCampaignMetrics {
  idCampaign: string;
  delivery: DeliveryMetrics;
  open: OpenMetrics;
  click: ClickMetrics;
  details: Record<string, CampaignMetricDetails>;
  score: number;
}

export interface SingleCampaignMetricsOverview {
  delivery: DeliveryMetrics;
  open: OpenMetrics;
  click: ClickMetrics;
}

export interface SingleMailingListMetricsOverview {
  numRecipients: number;
  numDelivered: number;
  numOpens: number;
  numClicks: number;
  numBounces: number;
  numUnsubscribes: number;
  numComplaints: number;
  score?: number;
}

export interface EmailMarketingCampaign {
  id: string;
  name: string;
  isDraft: boolean;
  isScheduled: boolean;
  publishOn: string;
}

export interface OptimalTimeResponse {
  maximumUtcTimestamp: number;
  minimumUtcTimestamp: number;
}

export interface MaxOptimalTimePayload {
  idEmailMarketingCampaign: string;
  clientTimezoneOffset: number;
  scheduledDate?: string;
}

export interface UserMailingList {
  id: number;
  name: string;
  createdOn: string;
  numEmails?: number;
  lastViewed?: string;
  numNonDeletedEmails?: number;
  isActive?: boolean;
  validEmailsCount?: number;
  lastUpdatedOn?: string;
  mailingListContacts?: RawAudience[];
  isSmart: boolean;
  isSpam: boolean;
  numNewContacts?: number;
  isSharedInTeam: boolean;
  idOwner: number;
}

export interface MailingListMetricsInsight {
  email: string;
  numClicks: number;
  numOpens: number;
  opens: MetricInsightsPopupMenuItem[];
  links: MetricInsightsPopupMenuItem[];
  isUnsubscribe: boolean;
  isBounce: boolean;
  isDeleted: boolean;
}

export interface MailingListMetrics {
  idMailingList: number;
  numRecipients: number;
  numDelivered: number;
  numOpens: number;
  numClicks: number;
  numBounces: number;
  numUnsubscribes: number;
  numComplaints: number;
  insights: Record<string, MailingListMetricsInsight>;
  score: number;
}

export interface CampaignPerformance {
  performanceLoadingState: number;
  selectedItemIds: string[];
  isCollectingData: boolean;
  itemType: MetricItemType.CAMPAIGN | MetricItemType.AUDIENCE;
}

export interface EmailMarketingCampaignWithMailingLists extends CampaignPerformance {
  itemType: MetricItemType.AUDIENCE;
  mailingLists: Record<string, UserMailingList>;
  mailingListsWithAvailableMetrics: Record<string, number>;
  mailingListsWithMetricsNotAvailable: Record<string, number>;
  mailingListsWithMetricsCollecting: Record<string, number>;
  mailingListMetrics: Record<string, MailingListMetrics>;
  isNoMailingListCreated?: boolean;
  isNotCollectingData?: boolean;
}

export interface EmailMarketingCampaignWithCampaigns extends CampaignPerformance {
  itemType: MetricItemType.CAMPAIGN;
  campaigns: Record<string, EmailMarketingCampaign>;
  campaignsWithMetricsAvailable: Record<string, string>;
  campaignsWithMetricsNotAvailable: Record<string, string>;
  campaignsWithMetricsCollecting: Record<string, string>;
  campaignMetrics: Record<string, SingleCampaignMetrics>;
  isNoCampaignCreated?: boolean;
  isNoCampaignPublished?: boolean;
}

export type CSVMetrics = SingleCampaignMetricsOverview & SingleMailingListMetricsOverview;
export type CurrentItemMetrics = MailingListMetrics | SingleCampaignMetrics;
export type CurrentItemDetails = CampaignMetricDetails[] | MailingListMetricsInsight[];
export type CurrentItemMetricsOverview = SingleCampaignMetricsOverview | SingleMailingListMetricsOverview;

export const DEFAULT_ITEM_ID = 'none';
export const COMPARISON_VIEW_SCREEN_WIDTH_BREAKPOINT_PX = 1000;

export interface EmailMarketingCampaignPerformanceState {
  performanceLoadingState: number;
  campaigns: Record<string, EmailMarketingCampaign>;
  campaignsWithMetricsAvailable: Record<string, string>;
  campaignsWithMetricsNotAvailable: Record<string, string>;
  campaignsWithMetricsCollecting: Record<string, string>;
  mailingLists: Record<string, UserMailingList>;
  mailingListsWithAvailableMetrics: Record<string, number>;
  mailingListsWithMetricsNotAvailable: Record<string, number>;
  mailingListsWithMetricsCollecting: Record<string, number>;
  campaignMetrics: Record<string, SingleCampaignMetrics>;
  mailingListMetrics: Record<string, MailingListMetrics>;
  selectedItemIds: string[];
  itemType: string;
  isNoCampaignCreated: boolean;
  isNoCampaignPublished: boolean;
  isCollectingData: boolean;
  isNoMailingListCreated: boolean;
  isNotCollectingData: boolean;
}

export interface InitEmailMarketingCampaignPerformanceArg {
  itemType: string;
  itemIds: string[];
}

export type InitEmailMarketingCampaignPerformanceResponse = Pick<
  EmailMarketingCampaignPerformanceState,
  | 'campaigns' | 'campaignsWithMetricsAvailable' | 'campaignsWithMetricsNotAvailable'
  | 'campaignsWithMetricsCollecting' | 'campaignMetrics' | 'mailingLists'
  | 'mailingListsWithAvailableMetrics' | 'mailingListsWithMetricsNotAvailable'
  | 'mailingListsWithMetricsCollecting' | 'mailingListMetrics' | 'itemType'
  | 'isNoCampaignCreated' | 'isNoCampaignPublished'
  | 'isCollectingData' | 'isNoMailingListCreated' | 'isNotCollectingData'
> & {
  selectedItemIds: (string | number)[];
};

export interface RemoveInvalidEmailsArg {
  id: string;
  emails: string[];
}

export interface PerformanceInputs {
  itemType: string;
  selectedItemIds: string[];
}
